// Companion/blocks/useChatBlockRenderer.tsx
import { useCallback } from 'react';
import type { ChatBlock } from '@/slices/chat/chatSlice';
import type { ChatBlockPlacement } from '@/types/chatStream';
import type { ChatSurface } from '@/types/chatEnvelope';
import ChatBlocks from './ChatBlocks';
import type { ChatQuestionFollowup } from './chatQuestionFollowup';

export interface UseChatBlockRendererOptions {
  surface: ChatSurface;
}

/**
 * The block registry, in the shape the thread's `renderBlocks` seam takes
 * (ORA-447). `ChatConversationView` defaults to this, so served content
 * renders everywhere a conversation does; a caller passing its own renderer —
 * a story, a test — still wins.
 *
 * The callback is stable for a given surface, because `ChatMessageItem` puts it
 * in a dependency list and a fresh function every render would re-render every
 * message in the thread.
 */
export const useChatBlockRenderer = ({
  surface,
}: UseChatBlockRendererOptions) =>
  useCallback(
    (
      blocks: ChatBlock[],
      placement: ChatBlockPlacement,
      onQuestionAnswered?: (question: ChatQuestionFollowup) => void,
      onBeforeContentExpand?: () => void
    ) => (
      <ChatBlocks
        blocks={blocks}
        placement={placement}
        surface={surface}
        onQuestionAnswered={onQuestionAnswered}
        onBeforeContentExpand={onBeforeContentExpand}
      />
    ),
    [surface]
  );
